import { useState } from "react";

import applyClassNames from "classnames";

import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import type { ContactDetail } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/enums";
import DropdownButton from "app/pages/workspace/sales/components/custom-dropdown/DropdownButton/DropdownButton";

import { StyledDropdownItem, StyledItemText } from "../EmployeeProfilesDropdown/DropdownItem/styles";
import { StyledDropdownContainer } from "../EmployeeProfilesDropdown/styles";

import { StyledDropdownBody } from "./styles";

type ContactDetailsDropdownProps = {
    isDisabled: boolean;
    types: readonly ContactDetail[];
    selectedType: ContactDetail | null;
    buttonText?: string;
    onSelect: (type: ContactDetail | null) => void;
    onOpen?: () => void;
    onClose?: () => void;
    isDropdownLeft?: boolean;
};

const ContactDetailsDropdown = (props: ContactDetailsDropdownProps) => {
    const {
        isDisabled,
        types,
        selectedType,
        onSelect,
        onOpen,
        onClose,
        buttonText = "si.account-overview.contact-details.dropdown-button.text",
        isDropdownLeft = false,
    } = props;

    const [isOpened, setIsOpened] = useState(false);
    const selectedText =
        selectedType === null ? null : translate(`si.account-overview.contact-details.dropdown.${selectedType}.text`);

    function handleOutsideClick() {
        setIsOpened(false);
        onClose?.();
    }

    useDeactivateOnOutsideClick("contact-details-dropdown", isOpened, handleOutsideClick);

    function handleSelectionReset() {
        onSelect(null);
    }

    function handleItemClick(type: ContactDetail) {
        setIsOpened(false);
        onSelect(type);
    }

    function handleButtonClick() {
        setIsOpened(true);
        onOpen?.();
    }

    return (
        <StyledDropdownContainer
            className="contact-details-dropdown"
            data-automation="contact-details-dropdown-container"
        >
            <DropdownButton
                disabled={isDisabled}
                selectedText={selectedText}
                key="contact-details-dd-button"
                buttonText={translate(buttonText)}
                onClick={handleButtonClick}
                onClose={handleSelectionReset}
            />
            <StyledDropdownBody
                open={isOpened}
                isDropdownLeft={isDropdownLeft}
            >
                {types.map((type) => (
                    <StyledDropdownItem
                        key={type}
                        onClick={() => handleItemClick(type)}
                        className={applyClassNames({
                            "--selected": selectedType === type,
                        })}
                    >
                        <I18n
                            element={StyledItemText}
                            data-automation="contact-details-item"
                            translatable={{
                                key: `si.account-overview.contact-details.dropdown.${type}.text`,
                            }}
                        />
                    </StyledDropdownItem>
                ))}
            </StyledDropdownBody>
        </StyledDropdownContainer>
    );
};

export default ContactDetailsDropdown;
